import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Loader from './Loader';

const ProtectedRoute = ({ children, requiredUserType, requiresPremium }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get user data from context or localStorage
  const userData = user || JSON.parse(localStorage.getItem('user') || '{}');
  const userType = userData.user_type || userData.userType;
  const isPremium = userData.isPremium || userData.premium_status === 'active';

  // Prevent back button navigation to regular dashboards for premium users
  useEffect(() => {
    // If this is a regular dashboard and user is premium, redirect immediately
    if (requiresPremium === false && isPremium && isAuthenticated) {
      const premiumPath = userType === 'seller' ? '/premium-seller-dashboard' : '/premium-buyer-dashboard';
      navigate(premiumPath, { replace: true });
    }
  }, [location.pathname, isPremium, requiresPremium, userType, navigate, isAuthenticated]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'var(--bg-light, #ffffff)'
      }}>
        <Loader />
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }


  // Check if user type matches required type
  if (requiredUserType && userType !== requiredUserType) {

    // Redirect to appropriate dashboard based on user's actual type
    if (userType === 'seller') {
      return <Navigate to={isPremium ? "/premium-seller-dashboard" : "/seller-dashboard"} replace />;
    } else if (userType === 'buyer') {
      return <Navigate to={isPremium ? "/premium-buyer-dashboard" : "/buyer-dashboard"} replace />;
    } else if (userType === 'admin') {
      return <Navigate to="/admin-dashboard" replace />;
    }
    return <Navigate to="/homepage" replace />;
  }

  // Check premium status
  if (requiresPremium === true && !isPremium) {
    // User needs premium but doesn't have it - redirect to regular dashboard
    if (userType === 'seller') {
      return <Navigate to="/seller-dashboard" replace />;
    } else if (userType === 'buyer') {
      return <Navigate to="/buyer-dashboard" replace />;
    }
    return <Navigate to="/homepage" replace />;
  }

  // Check if premium user is trying to access regular dashboard
  if (requiresPremium === false && isPremium) {
    // Premium user trying to access regular dashboard - redirect to premium
    if (userType === 'seller') {
      return <Navigate to="/premium-seller-dashboard" replace />;
    } else if (userType === 'buyer') {
      return <Navigate to="/premium-buyer-dashboard" replace />;
    }
  }

  // All checks passed, render the protected content
  return children;
};

export default ProtectedRoute;
